/**
 * CancelAttemptsBadge — Iter 156.34 (Feb 28, 2026, Yash) helper.
 *
 * Compact inline indicator that fetches the LAST `cancel_upstream_log`
 * row for a given `order_id` and shows colour-coded status:
 *
 *   🟢 success      — last upstream call returned status 1/2 (refund initiated / queued)
 *   🟠 noop / fail  — upstream replied but with error / status=0
 *   🔴 network err  — request never reached upstream (timeout, conn refused, WAF block)
 *   ⚪ no attempts  — no cancel ever tried for this order
 *
 * Click → expand a detail card with raw response body + elapsed ms +
 * timestamp + a link to the full audit page filtered by this order.
 *
 * NOTE: This component will be removed (along with the audit endpoint
 * and React page) once the upstream-cancel bug is identified per user
 * request. Keep the surface area small to make removal trivial.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { ExternalLink, RefreshCw } from "lucide-react";
import { adminApi } from "../../lib/api";

const PHASE_LABEL = {
  pre_send:             "Pre-send",
  post_send:            "Success",
  upstream_error:       "Upstream error",
  validation_failed:    "Validation failed",
  post_send_error:      "Network/transport error",
  unexpected_exception: "Unexpected exception",
};

function phaseTone(row) {
  if (!row) return { bg: "bg-slate-100", fg: "text-slate-600", ring: "ring-slate-200", emoji: "⚪" };
  const phase = row.phase;
  if (phase === "post_send") return { bg: "bg-emerald-50", fg: "text-emerald-700", ring: "ring-emerald-200", emoji: "🟢" };
  if (phase === "upstream_error" || phase === "validation_failed") return { bg: "bg-amber-50", fg: "text-amber-800", ring: "ring-amber-200", emoji: "🟠" };
  if (phase === "post_send_error" || phase === "unexpected_exception") return { bg: "bg-rose-50", fg: "text-rose-800", ring: "ring-rose-200", emoji: "🔴" };
  if (phase === "pre_send") return { bg: "bg-blue-50", fg: "text-blue-800", ring: "ring-blue-200", emoji: "🟡" };
  return { bg: "bg-slate-100", fg: "text-slate-600", ring: "ring-slate-200", emoji: "⚪" };
}

function fmtDT(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function CancelAttemptsBadge({ orderId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const fetchedRef = useRef(false);

  const fetchRows = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    try {
      // Pull last 5 — newest first. Only fetch this order's rows.
      const { data } = await adminApi.getCancelUpstreamLogs({
        order_id: String(orderId),
        limit: 5,
      });
      setRows(data?.rows || []);
    } catch (e) {
      // Silently degrade — this is a nice-to-have indicator. Don't toast
      // an error every time the user opens a chat.
      console.debug("[CancelAttemptsBadge] fetch failed", e?.message || e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  // Auto-fetch on first mount per orderId (lazy — only when the user
  // expands the order card).
  useEffect(() => {
    if (!fetchedRef.current) {
      fetchedRef.current = true;
      fetchRows();
    }
  }, [fetchRows]);

  const latest = rows[0] || null;
  const tone = phaseTone(latest);

  if (!orderId) return null;

  return (
    <div data-testid={`cancel-attempts-${orderId}`} className="mt-1.5">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold ring-1 ${tone.bg} ${tone.fg} ${tone.ring} hover:brightness-95 transition`}
        data-testid={`cancel-attempts-toggle-${orderId}`}
      >
        <span>{tone.emoji}</span>
        <span>
          {loading
            ? "Cancel: checking…"
            : latest
              ? `Cancel: ${PHASE_LABEL[latest.phase] || latest.phase}${rows.length > 1 ? ` (${rows.length})` : ""}`
              : "Cancel: no attempts"}
        </span>
      </button>

      {open && (
        <div className="mt-1.5 border border-slate-200 rounded-lg bg-white p-2 space-y-2 text-[10px]" data-testid={`cancel-attempts-details-${orderId}`}>
          <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-1.5">
            <span className="font-semibold text-slate-700">
              Upstream cancel attempts
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => { e.stopPropagation(); fetchRows(); }}
                className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-800"
                title="Refresh"
                data-testid={`cancel-attempts-refresh-${orderId}`}
              >
                <RefreshCw size={10} className={loading ? "animate-spin" : ""} />
                Refresh
              </button>
              <a
                href={`/admin/cancel-upstream-logs?order_id=${orderId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                data-testid={`cancel-attempts-open-page-${orderId}`}
              >
                Full audit
                <ExternalLink size={10} />
              </a>
            </div>
          </div>

          {rows.length === 0 ? (
            <p className="text-slate-500 italic">
              No cancel attempts logged. Either none was triggered, or this
              order pre-dates the audit instrumentation.
            </p>
          ) : (
            <div className="space-y-1.5">
              {rows.map((r) => {
                const t = phaseTone(r);
                return (
                  <div
                    key={r.id}
                    className={`rounded border ${t.ring} ${t.bg} px-1.5 py-1`}
                    data-testid={`cancel-attempt-row-${r.id}`}
                  >
                    <div className="flex items-center justify-between gap-2 font-mono">
                      <span className={`font-bold ${t.fg}`}>
                        {t.emoji} {PHASE_LABEL[r.phase] || r.phase}
                      </span>
                      <span className="text-slate-500 text-[9px]">
                        {fmtDT(r.logged_at)}
                        {r.elapsed_ms != null ? ` · ${Math.round(r.elapsed_ms)}ms` : ""}
                        {r.http_status != null ? ` · HTTP ${r.http_status}` : ""}
                      </span>
                    </div>
                    {r.itemcode && (
                      <p className="text-slate-600 mt-0.5">
                        Item: <span className="font-mono">{r.itemcode}</span>
                        {r.action ? ` · ${r.action}` : ""}
                      </p>
                    )}
                    {r.error && (
                      <p className="text-rose-700 mt-0.5 break-words">
                        {r.error.length > 200 ? r.error.slice(0, 200) + "…" : r.error}
                      </p>
                    )}
                    {r.parsed_data?.refund_id && (
                      <p className="text-emerald-700 mt-0.5">
                        Refund ID: <span className="font-mono">{r.parsed_data.refund_id}</span>
                      </p>
                    )}
                    {r.parsed_data?.message && !r.error && (
                      <p className="text-slate-600 mt-0.5">
                        Upstream: {r.parsed_data.message}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CancelAttemptsBadge;
